import { useCallback, useEffect, useRef, useState } from "react";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormElements from "./form-elements";
import update from "immutability-helper";
import SectionSettings from "./section-settings";
import { useDispatch, useSelector } from "react-redux";
import {
  getDefinedNamesRequest,
  getDefinedNamesSuccess,
  getFormSectionsRequest,
  getFormSectionsSuccess,
} from "../../../../../redux/form-templates/slice";
import Loading from "../../../../../components/loading/loading";
import Notification from "../../../../../components/notification/notification";
import SectionElement from "./section-element";
import ElementSettings from "./element-settings";
import {
  getClientFormSectionsRequest,
  getClientFormSectionsSuccess,
  getFormDefinedNamesRequest,
  getFormDefinedNamesSuccess,
} from "../../../../../redux/clients/slice";
import {
  reOrderFormSectionElementRequest,
  reOrderFormSectionRequest,
} from "../../../../../utils/APICalls";

interface SectionItem {
  id: string;
  order: number;
  title: string;
  introductionText: string;
  isActive: boolean;
  elements: any[];
}

interface props {
  data: any;
}

const HtmlForm = ({ data }: props) => {
  const dispatch = useDispatch();
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [filteredDefinedNames, setFilteredDefinedNames] = useState<string[]>(
    []
  );
  const [selectedDefinedNames, setSelectedDefinedNames] = useState<string[]>(
    []
  );
  const [selectedSection, setSelectedSection] = useState<any>();
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedElement, setSelectedElement] = useState<any>();
  const [showSectionSettings, setShowSectionSettings] = useState(false);
  const [showElementSettings, setShowElementSettings] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const { loading, formSections, definedNames } = useSelector(
    (state: any) => state.formTemplates
  );
  const {
    loading: clientLoading,
    formSections: clientFormSections,
    formDefinedNames,
  } = useSelector((state: any) => state.clients);

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        // Client Form Builder
        dispatch(getClientFormSectionsSuccess({ response: [] }));
        dispatch(getClientFormSectionsRequest({ id: data?.id }));
        dispatch(getFormDefinedNamesSuccess({ response: null }));
        dispatch(getFormDefinedNamesRequest({ id: data?.id }));
      } else {
        // Form Template Form Builder
        dispatch(getFormSectionsSuccess({ response: [] }));
        dispatch(getFormSectionsRequest({ id: data?.id }));
        dispatch(getDefinedNamesSuccess({ response: null }));
        dispatch(getDefinedNamesRequest({ id: data?.id }));
      }
    }
  }, [data]); // eslint-disable-line

  useEffect(() => {
    if (data) {
      if (data?.clientId) {
        setSections(clientFormSections);
      } else {
        setSections(formSections);
      }
    }
  }, [data, formSections, clientFormSections]);

  useEffect(() => {
    if (data && (definedNames || formDefinedNames)) {
      if (data?.clientId) {
        const names = formDefinedNames?.map((item: any) => item.name);
        setFilteredDefinedNames(names);
      } else {
        const names = definedNames?.map((item: any) => item.name);
        setFilteredDefinedNames(names);
      }
    }
  }, [data, definedNames, formDefinedNames]);

  useEffect(() => {
    if (sections && sections.length > 0) {
      filterSelectedDefinedNames(sections);
    }
  }, [sections]);

  const filterSelectedDefinedNames = (formSections: any) => {
    const definedNames: string[] = [];
    formSections?.forEach((sec: any) => {
      sec?.elements?.forEach((element: any) => {
        if (element?.definedName) {
          definedNames.push(element.definedName);
        }
        if (element?.childElements && element?.childElements.length > 0) {
          element?.childElements.forEach((child: any) => {
            if (child?.definedName) {
              definedNames.push(child.definedName);
            }
          });
        }
      });
    });
    setSelectedDefinedNames(definedNames);
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "CARD",
    drop: (item: any, monitor) => {
      if (item.card && item.card.title === "Section") {
        setSections((prev) => {
          const newSection: SectionItem = {
            id: "",
            order: prev.length + 1,
            title: "",
            introductionText: "",
            isActive: true,
            elements: [],
          };
          return [...prev, newSection];
        });
      }
      if (item?.section && item?.section?.order - 1 !== item?.index) {
        // Don't call reorder API if section dropped in the same order or index
        onReOrderSection(item?.section, item?.index);
      }
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const handleSectionChange = (
    event: React.SyntheticEvent,
    expanded: boolean,
    section: any
  ) => {
    setSelectedSection(section);
    setShowElementSettings(false);
    setShowSectionSettings(true);
    // setSelectedElement(null);
  };

  const handleSectionElementChange = (
    event: React.SyntheticEvent,
    expanded: boolean,
    element: any,
    section: any
  ) => {
    setSelectedSection(section);
    setSelectedElement(element);
    setShowElementSettings(true);
    setShowSectionSettings(false);
  };

  const onSectionSettingsSaveClick = (data: any) => {
    const updatedSections = JSON.parse(JSON.stringify(sections));
    updatedSections.forEach((section: any) => {
      if (section.order === data?.order) {
        section.id = data.id;
        section.title = data.title;
        section.introductionText = data.introductionText;
        section.isActive = data.isActive;
      }
    });
    setSections(updatedSections);
    setShowSuccessNotification(true);
    setSuccessMessage("Saved Section Successfully");
    setSelectedSection(null);
    setShowSectionSettings(false);
  };

  const onElementSettingsSaveClick = (data: any) => {
    const updatedSections = JSON.parse(JSON.stringify(sections));
    updatedSections.forEach((section: any) => {
      if (section?.id === selectedSection?.id) {
        section?.elements.forEach((element: any) => {
          if (element?.order === data?.order) {
            element.id = data?.id;
            element.isActive = data?.isActive;
            element.type = data?.type;
            element.definedName = data?.definedName;
            element.responseRequired = data?.responseRequired;
            element.childElements = data?.childElements;
            element.settings = { ...data?.settings };
          }
        });
      }
    });
    setSections(updatedSections);
    setShowSuccessNotification(true);
    setSuccessMessage(`Saved Successfully`);
    setSelectedElement(null);
    setShowElementSettings(false);
  };

  const onSectionSettingsCancelClick = () => {
    setShowSectionSettings(false);
    // setSelectedSection(null);
  };

  const onSectionSettingsDeleteClick = (data: any) => {
    const updatedSections = JSON.parse(JSON.stringify(sections));
    const sectionIndex = updatedSections.findIndex(
      (section: any) => section.id === data.id
    );
    updatedSections.splice(sectionIndex, 1);
    setSections(updatedSections);
    setShowSuccessNotification(true);
    setSuccessMessage("Deleted Section Successfully");
    setSelectedSection(null);
    setShowSectionSettings(false);
  };

  const onElementSettingsDeleteClick = (data: any) => {
    const updatedSections = JSON.parse(JSON.stringify(sections));
    updatedSections.forEach((section: any) => {
      if (section?.id === selectedSection?.id) {
        const elementIndex = section?.elements.findIndex(
          (element: any) => element.order === data.order
        );
        section?.elements.splice(elementIndex, 1);
        for (let i = elementIndex; i < section.elements.length; i++) {
          section.elements[i].order -= 1;
        }
      }
    });
    setSections(updatedSections);
    setShowSuccessNotification(true);
    setSuccessMessage("Deleted Successfully");
    setSelectedElement(null);
    setShowElementSettings(false);
  };

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number, section: SectionItem) => {
      setSections((prevCards) => {
        return update(prevCards, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevCards[dragIndex]],
          ],
        });
      });
    },
    []
  );

  const moveElementCard = useCallback(
    (dragIndex: number, hoverIndex: number, section: SectionItem) => {
      setSections((prevCards) => {
        const index = prevCards.findIndex(
          (prev) => prev?.order === section?.order
        );
        return update(prevCards, {
          [index]: {
            elements: {
              $splice: [
                [dragIndex, 1],
                [hoverIndex, 0, prevCards[index]?.elements[dragIndex]],
              ],
            },
          },
        });
      });
    },
    []
  );

  const onReOrderSection = async (section: SectionItem, index: number) => {
    const isClient = !!data?.clientId;
    setShowLoading(true);
    const response = await reOrderFormSectionRequest(
      data?.id,
      section?.id,
      index + 1,
      isClient
    );
    setShowLoading(false);
    if (response?.status === 204) {
      if (isClient) {
        dispatch(getClientFormSectionsRequest({ id: data?.id }));
      } else {
        dispatch(getFormSectionsRequest({ id: data?.id }));
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
      }}
    >
      <FormElements />

      <Box
        ref={drop}
        sx={{
          marginTop: 1,
          paddingBottom: 2,
          minHeight: "200px",
          backgroundColor: isOver ? "rgba(0,255,0,0.1)" : "#fafafa",
          transition: "all 0.3s",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <Box>
          {sections.length > 0 ? (
            sections.map((section, index) => (
              <SectionAccordion
                key={index}
                index={index}
                templateId={data?.id}
                section={section}
                isClient={!!data?.clientId}
                updateSection={(id, newChildren) => {
                  setSections((prev) =>
                    prev.map((sec) =>
                      sec?.id === id
                        ? {
                            ...sec,
                            elements: [
                              ...sec.elements,
                              {
                                id: newChildren?.id,
                                order: newChildren?.order,
                                title: "",
                                type: newChildren?.type,
                                isActive: true,
                              },
                            ],
                          }
                        : sec
                    )
                  );
                }}
                updateSectionElement={(id, element, index) => {
                  setSections((prev) =>
                    prev.map((sec) =>
                      sec?.id === id
                        ? {
                            ...sec,
                            elements: [
                              ...sec.elements.map((ele, eleIndex) =>
                                ele?.id === element?.id
                                  ? { ...ele, order: index + 1 }
                                  : { ...ele, order: eleIndex + 1 }
                              ),
                            ],
                          }
                        : sec
                    )
                  );
                }}
                onChange={handleSectionChange}
                moveCard={moveCard}
                onElementSelect={handleSectionElementChange}
                moveElementCard={moveElementCard}
                selectedElement={selectedElement}
              />
            ))
          ) : (
            <Typography color="text.secondary">
              Drop Layer 1 items here (Section)
            </Typography>
          )}
        </Box>
      </Box>
      <Box
        flex={1}
        sx={{
          borderLeft: 1,
          height: "100vh",
          borderColor: "#C4C4C4",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {showSectionSettings && (
          <SectionSettings
            data={selectedSection}
            templateId={data?.id}
            isClient={!!data?.clientId}
            onSave={onSectionSettingsSaveClick}
            onCancel={onSectionSettingsCancelClick}
            onDelete={onSectionSettingsDeleteClick}
          />
        )}
        {showElementSettings && (
          <ElementSettings
            data={selectedElement}
            section={selectedSection}
            templateId={data?.id}
            isClient={!!data?.clientId}
            definedNames={filteredDefinedNames}
            selectedDefinedNames={selectedDefinedNames}
            onSave={onElementSettingsSaveClick}
            onCancel={() => setShowElementSettings(false)}
            onDelete={onElementSettingsDeleteClick}
          />
        )}
      </Box>
      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          onClose={() => setShowSuccessNotification(false)}
          title={successMessage}
        />
      )}
      {(loading || clientLoading || showLoading) && <Loading />}
    </Box>
  );
};

const SectionAccordion: React.FC<{
  index: number;
  section: SectionItem;
  templateId: string;
  isClient: boolean;
  updateSection: (id: string, children: any) => void;
  updateSectionElement: (id: string, children: any, index: number) => void;
  onChange: (
    event: React.SyntheticEvent,
    expanded: boolean,
    section: any
  ) => void;
  moveCard: any;
  onElementSelect: any;
  moveElementCard: any;
  selectedElement: any;
}> = ({
  index,
  section,
  templateId,
  isClient,
  updateSection,
  updateSectionElement,
  onChange,
  moveCard,
  onElementSelect,
  moveElementCard,
  selectedElement,
}) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);

  const [forbidDrag, setForbidDrag] = useState(false);
  const [loading, setLoading] = useState(false);

  const [{ handlerId }, drop] = useDrop(
    () => ({
      accept: "CARD",
      drop: (item: any) => {
        if (item?.card && item?.card?.title !== "Section") {
          const uniqueCard = {
            id: `element${section?.elements?.length}`,
            order: section?.elements?.length + 1,
            title: "",
            type: item?.card?.type,
            isActive: true,
          };
          updateSection(section.id, uniqueCard);
        }
        if (
          section?.id === item?.sectionId &&
          item?.element &&
          item?.element?.order - 1 !== item?.index
        ) {
          onReOrderElement(item?.element, item?.index);
        }
      },
      collect: (monitor: any) => ({
        handlerId: monitor.getHandlerId(),
      }),
      hover(item: any, monitor) {
        if (!ref.current) {
          return;
        }

        if (item?.card) {
          // if new section is dragged then don't perform reorder
          return;
        }

        if (item?.element) {
          // if section element is dragged and dropped then don't perform reorder
          return;
        }

        const dragIndex = item?.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;
        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }
        moveCard(dragIndex, hoverIndex, section);
        item.index = hoverIndex;
      },
    }),
    [section]
  );

  const [{ opacity }, drag] = useDrag(
    {
      type: "CARD",
      item: () => {
        return { section, index };
      },
      canDrag: !forbidDrag,
      collect: (monitor: any) => ({
        opacity: monitor.isDragging() ? 0.8 : 1,
      }),
    },
    [forbidDrag, section]
  );
  drag(drop(ref));

  const onReOrderElement = async (element: any, index: number) => {
    if (element?.id.includes("element")) {
      updateSectionElement(section?.id, element, index);
    } else {
      setLoading(true);
      const response = await reOrderFormSectionElementRequest(
        templateId,
        section?.id,
        element?.id,
        index + 1,
        isClient
      );
      setLoading(false);
      if (response?.status === 204) {
        if (isClient) {
          dispatch(getClientFormSectionsRequest({ id: templateId }));
        } else {
          dispatch(getFormSectionsRequest({ id: templateId }));
        }
      }
    }
  };

  const setDropRefs = (node: HTMLDivElement) => {
    drop(node);
  };

  return (
    <Box mt={4} mx={3}>
      <Accordion
        ref={setDropRefs}
        data-handler-id={handlerId}
        sx={{
          p: 1,
          border: "1px solid #E0E0E0",
          boxShadow: "none",
          backgroundColor: "white",
        }}
        onChange={(e, expanded) => {
          setForbidDrag(expanded);
          onChange(e, expanded, section);
        }}
      >
        <AccordionSummary
          ref={ref}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1,
            px: 1.4,
            m: 0,
            opacity: opacity,
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${index}-content`}
          id={`panel${index}-header`}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              {section?.title || `Section Title`}
            </Typography>
            <Typography sx={{ mr: 1 }}>
              {section?.elements?.length > 0
                ? `${section?.elements?.length} Questions`
                : ""}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Typography>{section?.introductionText || ""}</Typography>
          <Box>
            {section?.elements?.length > 0 &&
              section?.elements.map((item, idx) => (
                <SectionElement
                  key={idx}
                  index={idx}
                  section={section}
                  element={item}
                  isSelected={item?.order === selectedElement?.order}
                  onChange={onElementSelect}
                  moveCard={moveElementCard}
                />
              ))}
          </Box>
        </AccordionDetails>
      </Accordion>
      {loading && <Loading />}
    </Box>
  );
};

export default HtmlForm;
